import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { employeesAPI } from '../lib/api';
import { toast } from 'sonner';

// Company options must mirror the full Employee form (EmployeeDetail.jsx).
const COMPANY_OPTIONS = ['Security', 'Surveillance', 'Cyber', 'Exquisite', 'Other'];

/**
 * Quick-add employee dialog. Stays in the current screen context.
 * On save, calls onCreated(newEmployee) so the caller can refresh and auto-select.
 *
 * Fields match the full Employee form (EmployeeDetail.jsx):
 *  - first_name, last_name (required)
 *  - employee_number, email
 *  - company (Select, same options as full form)
 *  - position (free text)
 *  - assignment_location (free text, e.g. "Downtown Office, Site B")
 */
export function EmployeeQuickAddDialog({ open, onOpenChange, onCreated, initialName = '' }) {
  const empty = {
    first_name: '',
    last_name: '',
    employee_number: '',
    email: '',
    company: '',
    position: '',
    assignment_location: '',
    hire_date: '',
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Best-effort split of initial typed name into first/last
      const parts = (initialName || '').trim().split(/\s+/);
      setForm({
        ...empty,
        first_name: parts[0] || '',
        last_name: parts.slice(1).join(' ') || '',
      });
    }
  }, [open, initialName]);

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        employee_number: form.employee_number.trim() || null,
        email: form.email.trim() || null,
        company: form.company || null,
        position: form.position.trim() || null,
        assignment_location: form.assignment_location.trim() || null,
        hire_date: form.hire_date || null,
        is_active: true,
      };
      const res = await employeesAPI.create(payload);
      toast.success(`Added ${res.data.first_name} ${res.data.last_name}`);
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create employee');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="employee-quick-add-dialog">
        <DialogHeader>
          <DialogTitle>Add New Employee</DialogTitle>
          <DialogDescription>Quick-add. You can edit the full profile from the Employees page later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>First Name *</Label>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                autoFocus
                data-testid="quick-add-first-name"
              />
            </div>
            <div className="space-y-1">
              <Label>Last Name *</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                data-testid="quick-add-last-name"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Employee #</Label>
            <Input
              value={form.employee_number}
              onChange={(e) => setForm({ ...form, employee_number: e.target.value })}
              data-testid="quick-add-employee-number"
            />
          </div>
          <div className="space-y-1">
            <Label>Email</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="quick-add-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Company</Label>
              <Select
                value={form.company}
                onValueChange={(value) => setForm({ ...form, company: value })}
              >
                <SelectTrigger data-testid="quick-add-company">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_OPTIONS.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Position</Label>
              <Input
                value={form.position}
                onChange={(e) => setForm({ ...form, position: e.target.value })}
                placeholder="Security Officer"
                data-testid="quick-add-position"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Assignment Location</Label>
            <Input
              value={form.assignment_location}
              onChange={(e) => setForm({ ...form, assignment_location: e.target.value })}
              placeholder="e.g. Downtown Office, Site B"
              data-testid="quick-add-assignment-location"
            />
          </div>
          <div className="space-y-1">
            <Label>Hire Date</Label>
            <Input
              type="date"
              value={form.hire_date}
              onChange={(e) => setForm({ ...form, hire_date: e.target.value })}
              data-testid="quick-add-hire-date"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="quick-add-save-btn">
              {saving ? 'Saving...' : 'Save Employee'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default EmployeeQuickAddDialog;
